/**
 * Session History Redux Slice
 * State management for session history feature
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ISessionHistoryState } from '../../interface/sessionHistoryModel';
import { fetchSessionHistory } from '../action/sessionHistory.action';

/**
 * Initial state for session history
 */
const initialState: ISessionHistoryState = {
  // Data States
  sessions: [],
  totalSessions: 0,
  currentPage: 1,
  pageSize: 5,

  // Metrics (from API summary)
  totalCollected: 0,
  totalRefunded: 0,
  activeSessions: 0,

  // UI States
  isLoading: false,
  error: null,

  // Filter States
  filters: {
    status: null,
    startDate: null,
    endDate: null,
    cashierName: '',
  },
};

/**
 * Session History Slice
 */
const sessionHistorySlice = createSlice({
  name: 'sessionHistory',
  initialState,
  reducers: {
    /**
     * Update filter state
     */
    setFilters: (state, action: PayloadAction<Partial<ISessionHistoryState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    /**
     * Reset all filters to initial state
     */
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.currentPage = 1;
    },

    /**
     * Set current page number
     */
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },

    /**
     * Set page size
     */
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1; // Reset to first page when page size changes
    },

    /**
     * Reset entire session history state to initial values
     * Used when component unmounts or needs clean slate
     */
    resetSessionHistoryState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch session history - pending
      .addCase(fetchSessionHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      // Fetch session history - fulfilled
      .addCase(fetchSessionHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sessions = action.payload.data.sessions;
        state.totalSessions = action.payload.data.total;
        state.currentPage = action.payload.data.currentPage;
        state.pageSize = action.payload.data.pageSize;

        // Use summary metrics from API response
        state.totalCollected = action.payload.data.summary.totalCollected;
        state.totalRefunded = action.payload.data.summary.totalRefund;
        state.activeSessions = action.payload.data.summary.activeSessions;
      })
      // Fetch session history - rejected
      .addCase(fetchSessionHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setFilters, resetFilters, setCurrentPage, setPageSize, resetSessionHistoryState } =
  sessionHistorySlice.actions;

export default sessionHistorySlice.reducer;
